import { inject } from '@angular/core';
import { CanActivateFn, createUrlTreeFromSnapshot, Router, UrlTree } from '@angular/router';

import { first, map, Observable } from 'rxjs';

import { ROUTES } from '../definitions/constants/routes.constants';
import { ViewEnum } from '../definitions/enums/settings/view.enum';
import { ViewAccessReason } from '../definitions/enums/switch-reasons/view-access.reason';
import { ReplenishmentCurrentData } from '../definitions/interfaces/user/replenishment-current-data';
import { User } from '../definitions/interfaces/user/user';
import { RouterStoreFacade } from '../stores/global-store/router/router-store.facade';
import { UserConfigsStoreFacade } from '../stores/global-store/user-configs/user-configs-store.facade';

// Dev Note: Requirements from https://jira.bbraun.com/browse/W39TOM-12056
export const replenishmentGuard: CanActivateFn = (routeSnapshot): Observable<UrlTree | boolean> => {
  const userConfigsFacade = inject(UserConfigsStoreFacade);
  const routerFacade = inject(RouterStoreFacade);
  const router = inject(Router);

  return userConfigsFacade.user$.pipe(
    first((user): user is User => !!user),
    map((user: User) => {
      if (!userConfigsFacade.hasReplenishmentAccessSignal()) return router.createUrlTree([ ROUTES.MAIN ]);

      let accessReason;
      let defaultView;
      let defaultCountry;
      let defaultCustomer;

      if (localStorage.getItem('access-reason')) {
        accessReason = localStorage.getItem('access-reason') as ViewAccessReason;
        localStorage.removeItem('access-reason');
      }

      if (userConfigsFacade.isInternalUserSignal()) {
        defaultView = ViewEnum.INTERNAL;
        defaultCountry = userConfigsFacade.isSuperAdminSignal() ? 'FR' : userConfigsFacade.userCountriesSignal()[0];
      }

      if (userConfigsFacade.isExternalUserSignal()) {
        defaultView = ViewEnum.EXTERNAL;
        defaultCountry = userConfigsFacade.userCountriesSignal()[0];
        defaultCustomer = userConfigsFacade.allReplenishmentCustomersSignal()[0];
      }

      const possibleCustomerErpRef = routerFacade.currentCustomerErpRefParam() ?? defaultCustomer;

      const currenData: ReplenishmentCurrentData = {
        view: routerFacade.currentViewParam() ?? defaultView,
        countryCode: routerFacade.currentCountryCodeParam() ?? defaultCountry
      };

      // Dev note: if the user is super admin the customerErpRef is undefined
      if (possibleCustomerErpRef !== undefined) {
        currenData.customerErpRef = possibleCustomerErpRef;
      }

      if (!userConfigsFacade.replenishmentStartedSignal()) userConfigsFacade.startReplenishment(user, currenData, accessReason);
      if (routerFacade.hasRequiredReplenishmentParams()) return true;

      return createUrlTreeFromSnapshot(routeSnapshot, [], { ...routeSnapshot.queryParams, ...currenData });
    })
  );
};
